import React, { createContext, useContext, useState, useMemo, useCallback } from 'react';
import {
  calculateEstimate,
  type EstimatorSelections,
  type EstimateResult,
} from '@/data/pricing';

interface EstimatorContextType {
  isOpen: boolean;
  openEstimator: (initialService?: string) => void;
  closeEstimator: () => void;
  step: number;
  setStep: (step: number) => void;
  nextStep: () => void;
  prevStep: () => void;
  selections: EstimatorSelections;
  toggleService: (serviceId: string) => void;
  isServiceSelected: (serviceId: string) => boolean;
  setUgcVideoCount: (count: number) => void;
  resetEstimator: () => void;
  estimate: EstimateResult;
  canProceedToNext: boolean;
}

const defaultSelections: EstimatorSelections = {
  selectedServices: ['website'],
  ugcVideoCount: 1,
};

const EstimatorContext = createContext<EstimatorContextType | undefined>(undefined);

export const EstimatorProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [selections, setSelections] = useState<EstimatorSelections>(defaultSelections);

  const openEstimator = useCallback((initialService?: string) => {
    if (initialService) {
      setSelections((prev) => ({
        ...prev,
        selectedServices: prev.selectedServices.includes(initialService)
          ? prev.selectedServices
          : [initialService],
      }));
    }
    setStep(1);
    setIsOpen(true);
    document.body.style.overflow = 'hidden';
  }, []);

  const closeEstimator = useCallback(() => {
    setIsOpen(false);
    document.body.style.overflow = '';
  }, []);

  const nextStep = useCallback(() => {
    setStep((prev) => Math.min(prev + 1, 2));
  }, []);

  const prevStep = useCallback(() => {
    setStep((prev) => Math.max(prev - 1, 1));
  }, []);

  const toggleService = useCallback((serviceId: string) => {
    setSelections((prev) => {
      const exists = prev.selectedServices.includes(serviceId);
      const updated = exists
        ? prev.selectedServices.filter((id) => id !== serviceId)
        : [...prev.selectedServices, serviceId];
      return {
        ...prev,
        selectedServices: updated,
      };
    });
  }, []);

  const isServiceSelected = useCallback(
    (serviceId: string) => selections.selectedServices.includes(serviceId),
    [selections.selectedServices]
  );

  const setUgcVideoCount = useCallback((count: number) => {
    setSelections((prev) => ({
      ...prev,
      ugcVideoCount: count,
    }));
  }, []);

  const resetEstimator = useCallback(() => {
    setStep(1);
    setSelections(defaultSelections);
  }, []);

  const estimate = useMemo(() => {
    return calculateEstimate(selections);
  }, [selections]);

  const canProceedToNext = useMemo(() => {
    return selections.selectedServices.length > 0;
  }, [selections.selectedServices]);

  const value = {
    isOpen,
    openEstimator,
    closeEstimator,
    step,
    setStep,
    nextStep,
    prevStep,
    selections,
    toggleService,
    isServiceSelected,
    setUgcVideoCount,
    resetEstimator,
    estimate,
    canProceedToNext,
  };

  return <EstimatorContext.Provider value={value}>{children}</EstimatorContext.Provider>;
};

export const useEstimator = () => {
  const context = useContext(EstimatorContext);
  if (!context) {
    throw new Error('useEstimator must be used within an EstimatorProvider');
  }
  return context;
};
